import React, { Fragment, useCallback, useRef, useState } from 'react';
import { Text, AcceptIcon } from '@fluentui/react-northstar';
import FilterCSS from './NestedFilter.module.css';
import { INestedFilterItem } from "./NestedFilter";

interface ITreeItem {
  id: number;
  value: string;
  isSelected?: boolean;
  isRoving: boolean;
  paddingLeft: string;
  onSelectCallback: () => void;
  onFocusItem: () => void;
}

const MENU_ITEM_SELECTOR = '[role="menuitemcheckbox"]';

export const RecursiveTree = ({ items, onSelectCallback }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rovingKey, setRovingKey] = useState<number | undefined>(undefined);
  const effectiveRovingKey = rovingKey ?? items[0]?.key;

  const createTree = (item: INestedFilterItem, padding: number) => {
    padding++;
    return item && <>
      <TreeItem
        id={item.key}
        key={item.key}
        onSelectCallback={() => { onSelectCallback(item) }}
        onFocusItem={() => setRovingKey(item.key)}
        value={item.title}
        isSelected={item.selected}
        isRoving={effectiveRovingKey === item.key}
        paddingLeft={`${padding * 0.75}rem`}
      />
      {item.children && item.children.map((child: any) => {
        return <Fragment key={child.key}>{createTree(child, padding)}</Fragment>;
      })}
    </>;
  }

  const focusItemAt = (menuItems: HTMLElement[], index: number) => {
    if (menuItems.length === 0) return;
    const nextIndex = (index + menuItems.length) % menuItems.length;
    menuItems[nextIndex]?.focus();
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const menuItems = Array.from(containerRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? []);
    const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        focusItemAt(menuItems, currentIndex === -1 ? 0 : currentIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        focusItemAt(menuItems, currentIndex === -1 ? menuItems.length - 1 : currentIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        e.stopPropagation();
        focusItemAt(menuItems, 0);
        break;
      case 'End':
        e.preventDefault();
        e.stopPropagation();
        focusItemAt(menuItems, menuItems.length - 1);
        break;
      case 'Enter':
      case ' ':
        if (currentIndex !== -1) {
          e.preventDefault();
          e.stopPropagation();
          (document.activeElement as HTMLElement).click();
        }
        break;
      default:
        break;
    }
  }, []);

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      {items.map((item: INestedFilterItem, i: any) => (
        <Fragment key={item.key}>{createTree(item, 0)}</Fragment>
      ))}
    </div>
  );
};

const TreeItem = ({ id, value, isSelected, isRoving, paddingLeft, onSelectCallback, onFocusItem }: ITreeItem) => {
  return (
    <div
      key={id}
      role="menuitemcheckbox"
      aria-checked={!!isSelected}
      tabIndex={isRoving ? 0 : -1}
      onFocus={onFocusItem}
      className={`${FilterCSS.filterOption} cursor-pointer`}
      onClick={() => { onSelectCallback() }}
    >
      <div style={{ paddingLeft: paddingLeft }} className={`flex justify-between pr-3 py-2`}>
        <Text content={value} weight="semibold" />
        {isSelected && (<Text color="brand" content={<AcceptIcon />} />)}
      </div>
    </div>
  );
};
